import { Component, Inject, OnInit } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MedicineService } from 'src/core/services/medicine.service';
import { CreateMedicineDosagePackageTypePayload, CreateMedicineDosageTypePayload, DosageType, DosageUnit, MedicineDosagePackageType, MedicineDosageType, PackageType } from 'src/interfaces/medicine';
import { MedicineDosageTypeEditDialogComponent } from '../medicine-dosage-type-edit-dialog/medicine-dosage-type-edit-dialog.component';

export interface DialogData {
  title: string;
  message: string;
  medicineId: number;
  dosageTypes: DosageType[];
  packageTypes: PackageType[];
  dosageUnits: DosageUnit[];
}
@Component({
  selector: 'core-medicine-dosage-type-create-dialog',
  templateUrl: './medicine-dosage-type-create-dialog.component.html',
  styleUrls: ['./medicine-dosage-type-create-dialog.component.scss']
})
export class MedicineDosageTypeCreateDialogComponent implements OnInit {

  medicineDosageTypeForm = this._formBuilder.group({
    additionalInformation: ['', Validators.required],
    usageInstructions: ['', Validators.required],
    storageDirections: ['', Validators.required],
    specialInstructions: ['', Validators.required],
    dosageStrength: ['', Validators.required],
    dosageType: ['', Validators.required],
    isActive: ['', Validators.required],
  });

  medicineDosagePackageTypeForm = this._formBuilder.group({
    packageType: ['', Validators.required],
    containedQuantity: ['', Validators.required],
    labelName: ["", Validators.compose([Validators.required, Validators.maxLength(25)])],
    price: ["", Validators.required]
  });

  medicineDosageType: MedicineDosageType | null = null;
  medicineDosagePackageTypes: MedicineDosagePackageType[] = [];

  dosageTypes: DosageType[];
  packageTypes: PackageType[];
  dosageUnits: DosageUnit[];
  currentDosageType: DosageType | null = null;
  currentDosageUnit: DosageUnit | null = null;
  labelNameExceeds25Chars: boolean = false;

  constructor(
    public _dialogRef: MatDialogRef<MedicineDosageTypeEditDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: DialogData,
    private _formBuilder: FormBuilder,
    private _medicineService: MedicineService
  ) { }

  ngOnInit(): void {
    this.packageTypes = this.data.packageTypes;
    this.dosageTypes = this.data.dosageTypes;
    this.dosageUnits = this.data.dosageUnits;


    this.medicineDosagePackageTypeForm.controls['labelName']
      .valueChanges.subscribe((value) => {
        this.labelNameExceeds25Chars = value.length > 25;
      })
  }

  async createMedicineDosageType() {
    const payload: CreateMedicineDosageTypePayload = {
      additional_information: this.medicineDosageTypeForm.value.additionalInformation,
      usage_instructions: this.medicineDosageTypeForm.value.usageInstructions,
      storage_directions: this.medicineDosageTypeForm.value.storageDirections,
      special_instructions: this.medicineDosageTypeForm.value.specialInstructions,
      dosage_strength: this.medicineDosageTypeForm.value.dosageStrength,
      dosage_type_id: this.medicineDosageTypeForm.value.dosageType.id,
      is_active: this.medicineDosageTypeForm.value.isActive
    }

    this.medicineDosageType = await this._medicineService
      .createMedicineDosageType(this.data.medicineId, payload);

    if (this.medicineDosageType) {

      this.currentDosageType = this.medicineDosageTypeForm.value.dosageType;

      const currentDosageUnit: DosageUnit | undefined = this.dosageUnits
        .find(dosageUnit => {
          if (!this.currentDosageType) return false;
          return dosageUnit.id === this.currentDosageType.dosage_unit_id
        });


      this.currentDosageUnit = currentDosageUnit ? currentDosageUnit : null;

    }
  }

  async createMedicineDosagePackageType() {

    if (!this.medicineDosageType) return;

    const payload: CreateMedicineDosagePackageTypePayload = {
      contained_quantity: this.medicineDosagePackageTypeForm.value.containedQuantity,
      package_type_id: this.medicineDosagePackageTypeForm.value.packageType.id,
      label_name: this.medicineDosagePackageTypeForm.value.labelName,
      price: this.medicineDosagePackageTypeForm.value.price
    }

    const medicineDosagePackageType: MedicineDosagePackageType | null = await this._medicineService
      .createMedicineDosagePackageType(
        this.data.medicineId,
        this.medicineDosageType.id,
        payload
      );

    if (medicineDosagePackageType && this.medicineDosageType) {

      this.medicineDosagePackageTypes = await this._medicineService
        .getMedicineDosagePackageTypes(
          this.data.medicineId,
          this.medicineDosageType.id,
          1, 10, ''
        )

      this.medicineDosagePackageTypeForm.reset();
    }
  }

  async onDosagePackageTypesUpdated(updated: boolean) {
    if (updated && this.medicineDosageType) {
      this.medicineDosagePackageTypes = await this._medicineService
        .getMedicineDosagePackageTypes(
          this.data.medicineId,
          this.medicineDosageType.id,
          1, 10, ''
        );
    }
  }

  async createPackageType(event: Event) {
    event.stopPropagation();
    const newPackageType: PackageType = await this._medicineService
      .openPackageTypeCreateDialog(
        "New package type",
        "Create new package type"
      );

    if (newPackageType) {
      // add dosage unit to the list dosage types
      this.packageTypes.push(newPackageType);
      // clone array 
      this.packageTypes = [...this.packageTypes];
      // Update the form value to the newly created dosage type
      this.medicineDosagePackageTypeForm.patchValue({
        packageType: newPackageType
      });
    }
  }

  async createDosageType(event: Event) {

    event.stopPropagation();

    const newDosageType: DosageType = await this._medicineService
      .openDosageTypeCreateDialog(
        "New dosage type",
        "Create new dosage type",
        this.dosageUnits
      );

    if (newDosageType) {
      // add dosage unit to the list dosage types
      this.dosageTypes.push(newDosageType);
      // clone array 
      this.dosageTypes = [...this.dosageTypes];
      // Update the form value to the newly created dosage type
      this.medicineDosageTypeForm.patchValue({
        dosageType: newDosageType
      });

    }
  }

  async deleteDosageType(type: DosageType) {
    const success: boolean = await this._medicineService.deleteDosageType(type.id);

    if (success)
      this.dosageTypes = this.dosageTypes
        .filter(dosageType => dosageType.id !== type.id);
  }

  async deletePackageType(type: PackageType) {
    const success: boolean = await this._medicineService.deletePackageType(type.id);

    if (success)
      this.packageTypes = this.packageTypes
        .filter(packageType => packageType.id !== type.id);
  }

  closeDialog() {
    this._dialogRef.close(this.medicineDosageType)
  }

}
